import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useLocationList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refLocationsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'image',
      label: 'location Image',
      sortable: false,
    },
    {
      key: 'name',
      label: 'Location name',
      tdClass: 'width-30',
      sortable: true,
    },
    {
      key: 'suiteArea.name',
      label: 'area',
      sortable: true,
    },
    {
      key: 'status',
      label: 'status',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalLocations = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const idSearchQuery = ref(route.query.idSearchQuery ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)
  const createdAtFilter = ref(route.query.createdAt || '')
  const statusFilter = ref([])
  const suiteAreaFilter = ref([])
  if (Array.isArray(route.query.suiteArea)) {
    suiteAreaFilter.value = route.query.suiteArea
  } else if (route.query.suiteArea) {
    suiteAreaFilter.value = [route.query.suiteArea]
  }
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refLocationsTable.value ? refLocationsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalLocations.value,
    }
  })

  const refetchData = () => {
    refLocationsTable.value.refresh()
  }

  // watch([searchQuery, idSearchQuery, statusFilter, createdAtFilter], () => {
  //   currentPage.value = 1
  //   refetchData()
  // }, { deep: true })

  // watch([currentPage], () => {
  //   refetchData()
  // }, { deep: true })

  const fetchLocations = () => {
    store
      .dispatch('app-suite-location/fetchLocations', {
        search: searchQuery.value,
        idSearchQuery: idSearchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        suiteArea: suiteAreaFilter.value,
        createdAt: createdAtFilter.value,
      },
      { root: true })
      .then(response => {
        const { locations, total } = response.data
        itemListData.value = locations
        currentDataLength.value = locations.length
        // callback(locations)
        totalLocations.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              idSearchQuery: idSearchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              suiteArea: suiteAreaFilter.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([statusFilter, suiteAreaFilter, createdAtFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchLocations()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchLocations()
      }
    }, 1000)
  }, { deep: true })

  watch([idSearchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 0)) {
        currentPage.value = 1
        fetchLocations()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchLocations()
  }, { deep: true })

  fetchLocations()

  const deleteLocation = () => {
    store
      .dispatch('app-suite-location/deleteLocation', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchLocations()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveLocationStatusVariant = status => {
    if (status === 'published') return 'success'
    if (status === 'draft') return 'secondary'
    return 'primary'
  }

  return {
    fetchLocations,
    tableColumns,
    perPage,
    currentPage,
    totalLocations,
    dataMeta,
    perPageOptions,
    idSearchQuery,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refLocationsTable,

    resolveLocationStatusVariant,
    refetchData,

    toDeletedID,
    deleteLocation,
    currentDataLength,
    itemListData,

    // Extra Filters
    statusFilter,
    suiteAreaFilter,
    createdAtFilter,
  }
}
